import { CommonError } from '@common/models/error.model';
import { useTranslation } from '@input-web/configs/locale/locale.config';
import mainStore, { useMainStore } from '@input-web/modules/main/stores/main.store';
import orderBy from 'lodash/orderBy';
import { useCallback, useMemo, useRef } from 'react';

/**
 * Hook for handling error dialog state and interactions
 * Manages error queue, translation, and dialog actions
 */
export default function useErrorDialogHandler() {
  // #region Dependencies
  const { t } = useTranslation();
  const { queryErrors } = useMainStore();
  // #endregion

  // #region Computed Values
  const isGenericErrorRef = useRef(false);
  const latestError = useMemo(() => {
    const error = orderBy(queryErrors, 'time', 'desc')?.[0];
    if (!error) return null;

    const genericMessage = t('error.generic.description');

    // Translate error if it's a CommonError instance
    if (error.error instanceof CommonError) {
      const translatedError = error.error.translate(t);
      isGenericErrorRef.current = translatedError.message === genericMessage;

      return { ...error, error: translatedError };
    }

    isGenericErrorRef.current = true;

    // Return generic error for unknown error types
    return {
      ...error,
      error: new CommonError({ message: genericMessage }),
    };
  }, [queryErrors, t]);
  // #endregion

  // #region Handlers
  const handleOnClose = useCallback(() => {
    mainStore.actions.setQueryErrors(queryErrors.filter(({ time }) => time !== latestError?.time));

    if (isGenericErrorRef.current) {
      window.location.reload();
      isGenericErrorRef.current = false;
    }
  }, [latestError, queryErrors]);
  // #endregion

  // #region Computed Values
  const actions = useMemo(
    () => ({ label: t('label.yes'), onClick: handleOnClose }),
    [handleOnClose, t],
  );
  // #endregion

  return {
    actions,
    content: latestError?.error.message,
    onClose: handleOnClose,
    open: !!latestError,
  };
}
